import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import P2PMarketplaceService from '../../services/p2pMarketplace.service';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  Divider,
  useMediaQuery,
  IconButton,
  Tooltip,
  LinearProgress,
  Skeleton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  Tab,
  Tabs,
  TabPanel,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack,
  Chat,
  Phone,
  Email,
  AccountBalance,
  Security,
  Speed,
  SupportAgent,
  Close,
  Refresh,
  Timer,
  AttachMoney,
  TrendingFlat,
  CheckCircle,
  Warning,
  Info,
  Cancel,
  Upload,
  Download,
  Visibility,
  VisibilityOff,
  Send,
  MoreVert,
  Star,
  StarBorder,
  Verified,
  LocationOn,
  AccessTime,
  Payment,
  AccountBalanceWallet,
  TrendingUp,
  TrendingDown,
  History,
  Settings,
  Help,
  Report,
  Block,
  Flag
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const P2POrderDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { orderId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentProof, setPaymentProof] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');

  useEffect(() => {
    loadOrderDetails();
  }, [orderId]);

  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await P2PMarketplaceService.getOrderDetails(orderId);
      if (response.success) {
        setOrder(response.order);
        // Load chat messages
        loadChatMessages(response.order.id);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const loadChatMessages = async (orderId) => {
    try {
      const response = await P2PMarketplaceService.getChatMessages(orderId);
      if (response.success) {
        setChatMessages(response.messages);
      }
    } catch (err) {
      console.error('Failed to load chat messages:', err);
    }
  };

  const sendChatMessage = async () => {
    if (!chatMessage.trim()) return;

    try {
      const response = await P2PMarketplaceService.sendChatMessage(order.id, chatMessage);
      if (response.success) {
        setChatMessages([...chatMessages, response.message]);
        setChatMessage('');
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handlePaymentProof = async () => {
    if (!paymentProof.trim()) return;

    try {
      const response = await P2PMarketplaceService.uploadPaymentProof(orderId, paymentProof);
      if (response.success) {
        setShowPaymentDialog(false);
        setPaymentProof('');
        loadOrderDetails();
      }
    } catch (err) {
      console.error('Failed to upload payment proof:', err);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) return;

    try {
      const response = await P2PMarketplaceService.cancelOrder(orderId, cancelReason);
      if (response.success) {
        setShowCancelDialog(false);
        setCancelReason('');
        loadOrderDetails();
      }
    } catch (err) {
      console.error('Failed to cancel order:', err);
    }
  };

  const handleDispute = async () => {
    if (!disputeReason.trim()) return;

    try {
      const response = await P2PMarketplaceService.createDispute(orderId, disputeReason);
      if (response.success) {
        setShowDisputeDialog(false);
        setDisputeReason('');
        loadOrderDetails();
      }
    } catch (err) {
      console.error('Failed to create dispute:', err);
    }
  };

  const formatPrice = (amount, currency = 'NGN') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatCryptoAmount = (amount, crypto) => {
    return `${parseFloat(amount).toFixed(8)} ${crypto}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'payment_pending': return 'warning';
      case 'payment_confirmed': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      case 'disputed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'payment_pending': return <Timer />;
      case 'payment_confirmed': return <CheckCircle />;
      case 'completed': return <CheckCircle />;
      case 'cancelled': return <Cancel />;
      case 'disputed': return <Warning />;
      default: return <Info />;
    }
  };

  const renderOrderInfo = () => (
    <Card sx={{ mb: 3, borderRadius: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Order #{order.order_id}
          </Typography>
          <Chip
            icon={getStatusIcon(order.status)}
            label={order.status.replace('_', ' ').toUpperCase()}
            color={getStatusColor(order.status)}
            variant="filled"
          />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Transaction Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Type:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {order.type === 'buy' ? 'Buy' : 'Sell'} {order.cryptocurrency}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Amount:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatPrice(order.amount, order.fiat_currency)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Crypto Amount:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCryptoAmount(order.crypto_amount, order.cryptocurrency)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Rate:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatPrice(order.rate, order.fiat_currency)} per {order.cryptocurrency}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Payment Method:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {order.payment_method}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Partner Details
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar
                sx={{ 
                  width: 48, 
                  height: 48,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '1.2rem',
                  fontWeight: 600
                }}
              >
                {order.partner.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {order.partner.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i}
                        sx={{ 
                          fontSize: 16,
                          color: i < Math.floor(order.partner.rating) ? 'warning.main' : 'grey.300'
                        }}
                      />
                    ))}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    ({order.partner.rating.toFixed(1)})
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Country:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {order.partner.country}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Total Trades:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {order.partner.total_trades}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Completion Rate:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {order.partner.completion_rate}%
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {order.terms && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Partner Terms
            </Typography>
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {order.terms}
              </Typography>
            </Paper>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderPaymentInfo = () => (
    <Card sx={{ mb: 3, borderRadius: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Payment Information
        </Typography>
        
        {order.type === 'buy' ? (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Please make payment to the partner using the details below. Upload payment proof after making payment.
            </Alert>
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Payment Details:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {order.payment_details || 'Payment details will be provided by the partner'}
              </Typography>
            </Paper>
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<Upload />}
                onClick={() => setShowPaymentDialog(true)}
                disabled={order.status !== 'payment_pending'}
              >
                Upload Payment Proof
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Please wait for the counterparty to make payment. The tokens for this sale will be transferred out of your Funding Account.
            </Alert>
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Payment Method: {order.payment_method}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {order.payment_details || 'Payment details will be provided by the partner'}
              </Typography>
            </Paper>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderChat = () => (
    <Card sx={{ mb: 3, borderRadius: 3 }}>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Chat with Partner
          </Typography>
        </Box>
        
        <Box sx={{ height: 400, overflowY: 'auto', p: 2 }}>
          {chatMessages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No messages yet. Start a conversation with your partner.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {chatMessages.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: message.sender_id === order.partner.id ? 'flex-start' : 'flex-end',
                    mb: 1
                  }}
                >
                  <Paper
                    sx={{
                      p: 2,
                      maxWidth: '70%',
                      bgcolor: message.sender_id === order.partner.id ? 'grey.100' : 'primary.main',
                      color: message.sender_id === order.partner.id ? 'text.primary' : 'white'
                    }}
                  >
                    <Typography variant="body2">
                      {message.message}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {new Date(message.created_at).toLocaleTimeString()}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        
        <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              placeholder="Type a message..."
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
              size="small"
            />
            <Button
              variant="contained"
              onClick={sendChatMessage}
              disabled={!chatMessage.trim()}
              startIcon={<Send />}
            >
              Send
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderActions = () => (
    <Card sx={{ mb: 3, borderRadius: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Actions
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {order.status === 'payment_pending' && order.type === 'buy' && (
            <Button
              variant="contained"
              startIcon={<Upload />}
              onClick={() => setShowPaymentDialog(true)}
            >
              Upload Payment Proof
            </Button>
          )}
          
          {['payment_pending', 'payment_confirmed'].includes(order.status) && (
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={() => setShowCancelDialog(true)}
              color="error"
            >
              Cancel Order
            </Button>
          )}
          
          {order.status === 'completed' && (
            <Button
              variant="contained"
              startIcon={<Star />}
              color="success"
            >
              Rate Partner
            </Button>
          )}
          
          <Button
            variant="outlined"
            startIcon={<Report />}
            onClick={() => setShowDisputeDialog(true)}
            color="error"
          >
            Report Issue
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/p2p')}
        >
          Back to P2P
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton 
          onClick={() => navigate('/p2p')}
          sx={{ 
            color: theme.palette.primary.main,
            '&:hover': { 
              bgcolor: `${theme.palette.primary.main}10`,
              transform: 'translateX(-2px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 600,
            color: theme.palette.text.primary,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          Order Details
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Order Info" />
          <Tab label="Payment" />
          <Tab label="Chat" />
          <Tab label="Actions" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && renderOrderInfo()}
      {activeTab === 1 && renderPaymentInfo()}
      {activeTab === 2 && renderChat()}
      {activeTab === 3 && renderActions()}

      {/* Payment Proof Dialog */}
      <Dialog open={showPaymentDialog} onClose={() => setShowPaymentDialog(false)}>
        <DialogTitle>Upload Payment Proof</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Payment Proof Details"
            value={paymentProof}
            onChange={(e) => setPaymentProof(e.target.value)}
            placeholder="Enter payment reference, transaction ID, or other proof details..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePaymentProof}>
            Upload Proof
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Order Dialog */}
      <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)}>
        <DialogTitle>Cancel Order</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for Cancellation"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Please provide a reason for cancelling this order..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleCancelOrder}>
            Cancel Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dispute Dialog */}
      <Dialog open={showDisputeDialog} onClose={() => setShowDisputeDialog(false)}>
        <DialogTitle>Report Issue</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Issue Description"
            value={disputeReason}
            onChange={(e) => setDisputeReason(e.target.value)}
            placeholder="Please describe the issue you're experiencing..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisputeDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDispute}>
            Report Issue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default P2POrderDetails;
